import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { feedAPI, wallAPI, hubAPI, notificationsAPI } from '@/lib/api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Bell, Filter, CheckCheck, Globe, AlertTriangle, Inbox } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useNavigate } from 'react-router-dom';
import { NotificationItem } from './NotificationItem';
import { NOTIFICATION_FILTERS, TYPE_TO_CATEGORY, notificationDestination } from './notificationMeta';

// Plusieurs cloches montées en même temps (sidebar repliée / dépliée, mobile) : on les resynchronise.
const SYNC_EVENT = 'notifications:changed';
const broadcast = () => { try { window.dispatchEvent(new Event(SYNC_EVENT)); } catch { /* noop */ } };

const clearPwaBadge = () => {
    if (typeof navigator !== 'undefined' && 'clearAppBadge' in navigator) {
        try { navigator.clearAppBadge(); } catch { /* noop */ }
    }
};

export const NotificationCenter = ({ bellTestId = 'notification-bell' }) => {
    const { user, refreshUnreadFeedCount, refreshWallUnread, wallUnreadItems } = useAuth();
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);   // non lues (liste)
    const [unseenCount, setUnseenCount] = useState(0);   // nouvelles (pastille)
    const [overdueCount, setOverdueCount] = useState(0);
    const [overdueScope, setOverdueScope] = useState('self');
    const [uatuAlerts, setUatuAlerts] = useState({ unseen: 0, critical: 0, total: 0 });
    const [open, setOpen] = useState(false);
    const [markingEverything, setMarkingEverything] = useState(false);
    const [tab, setTab] = useState('unread'); // 'unread' | 'all'
    const [filter, setFilter] = useState('all');

    const loadOverdueCount = useCallback(async () => {
        try {
            const res = await api.get('/tasks/my/overdue-count');
            setOverdueCount(res.data?.count || 0);
            setOverdueScope(res.data?.scope || 'self');
        } catch { /* silencieux */ }
    }, []);

    const loadUatuAlerts = useCallback(async () => {
        try {
            const res = await api.get('/uatu/observations');
            const obs = res.data?.observations || [];
            const unseen = obs.filter((o) => !o.seen_at);
            setUatuAlerts({ unseen: unseen.length, critical: unseen.filter((o) => o.severity === 'critical').length, total: obs.length });
        } catch { /* silencieux */ }
    }, []);

    const loadNotifications = useCallback(async () => {
        try {
            const res = await notificationsAPI.getAll(100);
            setNotifications(res.data.notifications || []);
            setUnreadCount(res.data.unread_count || 0);
            setUnseenCount(res.data.unseen_count || 0);
        } catch (error) {
            console.error('Error loading notifications:', error);
        }
    }, []);

    useEffect(() => {
        if (!user) return undefined;
        loadNotifications(); loadOverdueCount(); loadUatuAlerts();
        const interval = setInterval(() => { loadNotifications(); loadOverdueCount(); loadUatuAlerts(); }, 30000);
        const onVisible = () => { if (document.visibilityState === 'visible') loadNotifications(); };
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener(SYNC_EVENT, loadNotifications);
        return () => {
            clearInterval(interval);
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener(SYNC_EVENT, loadNotifications);
        };
    }, [user, loadNotifications, loadOverdueCount, loadUatuAlerts]);

    // Ouverture : la pastille tombe à 0 (« vu »), les notifications restent NON LUES.
    const handleOpenChange = async (nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) return;
        setTab(unreadCount > 0 ? 'unread' : 'all');
        if (unseenCount > 0) {
            setUnseenCount(0);
            setNotifications(prev => prev.map(n => ({ ...n, is_seen: true })));
            if (uatuAlerts.unseen === 0) clearPwaBadge();
            try { await notificationsAPI.markSeen(); broadcast(); } catch (e) { console.warn('mark-seen failed', e); }
        }
    };

    const markAllAsRead = async () => {
        setNotifications(prev => prev.map(n => ({ ...n, is_read: true, is_seen: true })));
        setUnreadCount(0); setUnseenCount(0);
        try {
            await notificationsAPI.markAllRead();
            broadcast();
            toast.success('Toutes les notifications sont marquées comme lues', { duration: 2000 });
        } catch {
            toast.error('Impossible de tout marquer comme lu');
            loadNotifications();
        }
    };

    // « Tout marquer lu partout » : cloche + fil + Hub + murs prospects.
    const markEverythingRead = async () => {
        if (markingEverything) return;
        setMarkingEverything(true);
        try {
            const wallItems = wallUnreadItems || [];
            const results = await Promise.allSettled([
                notificationsAPI.markAllRead(),
                feedAPI.markAllRead(),
                hubAPI.markActivitySeen(),
                ...wallItems.map(it => wallAPI.markRead(it.prospect_id, it.module)),
            ]);
            const failed = results.filter(r => r.status === 'rejected').length;
            setNotifications(prev => prev.map(n => ({ ...n, is_read: true, is_seen: true })));
            setUnreadCount(0); setUnseenCount(0);
            try { window.dispatchEvent(new Event('hub:activity-seen')); } catch { /* noop */ }
            try { window.dispatchEvent(new Event('hub:wall-seen')); } catch { /* noop */ }
            try { await refreshUnreadFeedCount(); } catch { /* noop */ }
            try { await refreshWallUnread(); } catch { /* noop */ }
            clearPwaBadge();
            broadcast();
            if (failed === 0) toast.success('Tout est marqué comme lu', { duration: 2000 });
            else toast.warning(`Marqué comme lu (${failed} module(s) ont échoué)`, { duration: 2500 });
        } catch (error) {
            console.error('Error markEverythingRead:', error);
            toast.error('Impossible de tout marquer comme lu');
        } finally {
            setMarkingEverything(false);
        }
    };

    const deleteNotification = async (notificationId, e) => {
        e.stopPropagation();
        const target = notifications.find(n => n.id === notificationId);
        setNotifications(prev => prev.filter(n => n.id !== notificationId));
        if (target && !target.is_read) setUnreadCount(prev => Math.max(0, prev - 1));
        try {
            await notificationsAPI.delete(notificationId);
            broadcast();
        } catch (error) {
            console.error('Error deleting notification:', error);
            loadNotifications();
        }
    };

    // Clic : passe en « lue » (jamais supprimée) puis navigue vers la cible.
    const handleNotificationClick = (notification) => {
        if (!notification.is_read) {
            setNotifications(prev => prev.map(n => n.id === notification.id ? { ...n, is_read: true, is_seen: true } : n));
            setUnreadCount(prev => Math.max(0, prev - 1));
            notificationsAPI.markRead(notification.id).then(broadcast).catch(() => loadNotifications());
        }
        const dest = notificationDestination(notification);
        if (dest) navigate(dest);
        setOpen(false);
    };

    const visible = useMemo(() => notifications.filter(n => {
        if (tab === 'unread' && n.is_read) return false;
        if (filter === 'all') return true;
        return (TYPE_TO_CATEGORY[n.type] || 'other') === filter;
    }), [notifications, tab, filter]);

    const badgeTotal = unseenCount + uatuAlerts.unseen;

    return (
        <Popover open={open} onOpenChange={handleOpenChange}>
            <PopoverTrigger asChild>
                <button
                    className={`relative flex h-11 w-11 items-center justify-center rounded-lg hover:bg-white/10 transition-colors ${uatuAlerts.critical > 0 ? 'uatu-bell-pulse-critical' : ''}`}
                    data-testid={bellTestId}
                    aria-label={badgeTotal > 0 ? `${badgeTotal} nouvelles notifications` : 'Notifications'}
                >
                    <Bell className="w-5 h-5 text-slate-400 hover:text-white transition-colors" />
                    {badgeTotal > 0 && (
                        <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center animate-pulse" data-testid="notif-unread-badge">
                            {badgeTotal > 99 ? '99+' : badgeTotal}
                        </span>
                    )}
                    {badgeTotal === 0 && unreadCount > 0 && (
                        <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-sky-400" data-testid="notif-unread-dot" title={`${unreadCount} non lue(s)`} />
                    )}
                </button>
            </PopoverTrigger>
            <PopoverContent
                className="w-[calc(100vw-1rem)] sm:w-[420px] p-0 shadow-xl overflow-hidden"
                align="end"
                sideOffset={8}
                collisionPadding={8}
                data-testid="notification-panel"
            >
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
                    <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-slate-900">Notifications</h3>
                        {unreadCount > 0 && (
                            <span className="hidden sm:inline-flex whitespace-nowrap rounded-full bg-sky-100 text-sky-700 text-[11px] font-semibold px-2 py-0.5" data-testid="notif-unread-count">
                                {unreadCount} non lue{unreadCount > 1 ? 's' : ''}
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-1">
                        <button
                            type="button"
                            onClick={markAllAsRead}
                            disabled={unreadCount === 0}
                            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-2 py-1 text-xs font-medium text-sky-700 hover:bg-sky-50 disabled:text-slate-300 disabled:hover:bg-transparent transition-colors"
                            data-testid="mark-all-read-btn"
                            title="Marquer toutes les notifications comme lues"
                        >
                            <CheckCheck className="w-3.5 h-3.5" />
                            Tout marquer lu
                        </button>
                        <button
                            type="button"
                            onClick={markEverythingRead}
                            disabled={markingEverything}
                            className="inline-flex items-center justify-center rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                            data-testid="mark-everything-read-btn"
                            title="Tout marquer lu partout : cloche, fil, projets et murs prospects"
                            aria-label="Tout marquer lu partout"
                        >
                            <Globe className={cn('w-4 h-4', markingEverything && 'animate-spin')} />
                        </button>
                    </div>
                </div>

                {uatuAlerts.unseen > 0 && (
                    <button
                        onClick={() => { setOpen(false); try { window.dispatchEvent(new CustomEvent('uatu:open-alerts')); } catch { /* noop */ } }}
                        className={`w-full flex items-center justify-between gap-2 px-4 py-2.5 border-b transition-colors text-left
                                    ${uatuAlerts.critical > 0 ? 'bg-violet-50 hover:bg-violet-100 border-violet-100' : 'bg-violet-50/50 hover:bg-violet-100/70 border-violet-100/70'}`}
                        data-testid="bell-uatu-banner"
                    >
                        <span className="flex items-start gap-2 text-sm font-medium text-violet-800">
                            <span className={`inline-block w-2 h-2 rounded-full mt-2 ${uatuAlerts.critical > 0 ? 'bg-red-500 animate-pulse' : 'bg-violet-500'}`} />
                            <span>
                                Uatu te sollicite sur {uatuAlerts.unseen} alerte{uatuAlerts.unseen > 1 ? 's' : ''}
                                {uatuAlerts.critical > 0 && (
                                    <span className="block text-[10px] font-semibold text-red-600 mt-0.5">
                                        {uatuAlerts.critical} critique{uatuAlerts.critical > 1 ? 's' : ''} · action recommandée
                                    </span>
                                )}
                            </span>
                        </span>
                        <span className="text-xs text-violet-700 shrink-0">Voir →</span>
                    </button>
                )}

                {overdueCount > 0 && (
                    <button
                        onClick={() => {
                            setOpen(false);
                            const scopeParam = overdueScope === 'all' ? '&scope=all' : overdueScope === 'team' ? '&scope=team' : '';
                            navigate(`/tasks?status=overdue${scopeParam}`);
                        }}
                        className="w-full flex items-center justify-between gap-2 px-4 py-2.5 bg-red-50 hover:bg-red-100 border-b border-red-100 transition-colors"
                        data-testid="bell-overdue-banner"
                    >
                        <span className="flex items-start gap-2 text-sm font-medium text-red-700 text-left">
                            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                            <span>
                                {overdueCount} tâche{overdueCount > 1 ? 's' : ''} en retard
                                <span className="block text-[10px] font-normal text-red-600/80 mt-0.5">
                                    {overdueScope === 'all' && 'Tous utilisateurs du CRM'}
                                    {overdueScope === 'team' && 'Toi + membres de ton équipe'}
                                    {overdueScope === 'self' && 'Uniquement les tâches où tu es assigné'}
                                </span>
                            </span>
                        </span>
                        <span className="text-xs text-red-600 shrink-0">Voir →</span>
                    </button>
                )}

                {/* Onglets Non lues / Toutes + filtre par type */}
                <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-slate-100 bg-slate-50">
                    <div className="inline-flex rounded-lg bg-slate-200/70 p-0.5" role="tablist" data-testid="notif-tabs">
                        {[
                            { key: 'unread', label: `Non lues${unreadCount > 0 ? ` (${unreadCount})` : ''}` },
                            { key: 'all', label: 'Toutes' },
                        ].map(t => (
                            <button
                                key={t.key}
                                type="button"
                                role="tab"
                                aria-selected={tab === t.key}
                                onClick={() => setTab(t.key)}
                                className={cn(
                                    'px-3 py-1 rounded-md text-xs font-medium transition-colors',
                                    tab === t.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800',
                                )}
                                data-testid={`notif-tab-${t.key}`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>
                    <div className="flex items-center gap-1.5">
                        <Filter className="w-3.5 h-3.5 text-slate-400" />
                        <Select value={filter} onValueChange={setFilter}>
                            <SelectTrigger className="h-7 w-[130px] text-xs bg-white" data-testid="notif-type-filter">
                                <SelectValue placeholder="Type" />
                            </SelectTrigger>
                            <SelectContent>
                                {NOTIFICATION_FILTERS.map(f => (
                                    <SelectItem key={f.value} value={f.value}>
                                        <div className="flex items-center gap-2"><f.icon className="w-3 h-3" />{f.label}</div>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <ScrollArea className="h-[min(420px,60vh)]">
                    {visible.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 px-4" data-testid="notif-empty-state">
                            <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mb-3">
                                {tab === 'unread' ? <CheckCheck className="w-7 h-7 text-emerald-500" /> : <Inbox className="w-7 h-7 text-slate-400" />}
                            </div>
                            <p className="text-slate-600 text-sm font-medium text-center">
                                {tab === 'unread' ? 'Vous êtes à jour' : filter === 'all' ? 'Aucune notification' : 'Aucune notification de ce type'}
                            </p>
                            <p className="text-xs text-slate-400 text-center mt-1">
                                {tab === 'unread' ? 'Aucune notification non lue' : 'Vous serez notifié des événements importants'}
                            </p>
                            {tab === 'unread' && notifications.length > 0 && (
                                <button type="button" onClick={() => setTab('all')} className="mt-3 text-xs text-sky-700 hover:underline" data-testid="notif-see-all-btn">
                                    Voir toutes les notifications
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="divide-y divide-slate-100">
                            {visible.map(n => (
                                <NotificationItem key={n.id} notification={n} onOpen={handleNotificationClick} onDelete={deleteNotification} />
                            ))}
                        </div>
                    )}
                </ScrollArea>
            </PopoverContent>
        </Popover>
    );
};
